import {
  MetricsV1AggregationThresholdDirection,
  MetricsV1AggregationType,
  MetricsV1MetricAverageMetricSpec,
  MetricsV1MetricMetricTypeSpec,
  MetricsV1MetricQuantileMetricSpec,
  MetricsV1MetricRatioMetricSpec,
} from '@spotify/confidence-graphql';

export type MetricType =
  | 'average'
  | 'ratio'
  | 'conversion'
  | 'consumption'
  | 'ctr'
  | 'quantile'
  | 'unknown';

export const metricTypeName: Record<MetricType, string> = {
  average: 'Average / Share',
  ratio: 'Ratio',
  conversion: 'Conversion',
  consumption: 'Consumption',
  ctr: 'Click-through rate',
  quantile: 'Percentile',
  unknown: 'Unknown',
};

type MetricTypeInput = {
  labels?: Array<{ key: string; value?: string | null }> | null;
  typeSpec?: {
    averageMetricSpec?: unknown;
    ratioMetricSpec?: unknown;
    quantileMetricSpec?: unknown;
  } | null;
};

export const getMetricType = (metric: MetricTypeInput): MetricType => {
  const metricType = metric.labels?.find(
    label => label.key === 'metricType',
  )?.value;
  if (metricType) {
    return metricType as MetricType;
  }
  if (metric.typeSpec?.quantileMetricSpec) {
    return 'quantile';
  }
  if (metric.typeSpec?.ratioMetricSpec) {
    return 'ratio';
  }
  if (metric.typeSpec?.averageMetricSpec) {
    return 'average';
  }
  return 'unknown';
};

export const getMetricTypeDisplayLabel = (metric: MetricTypeInput): string => {
  const type = getMetricType(metric);
  return metricTypeName[type] || type;
};

export const getQuantileLabel = (metric: MetricTypeInput): string | null => {
  const spec = metric.typeSpec?.quantileMetricSpec as
    | { quantileLevel?: number }
    | undefined;
  if (!spec?.quantileLevel) return null;
  return `P${Math.round(spec.quantileLevel * 100)}`;
};

type AverageTypeSpec = { averageMetricSpec: MetricsV1MetricAverageMetricSpec };
export const defaultAverageSpec: AverageTypeSpec = {
  averageMetricSpec: {
    aggregation: { type: MetricsV1AggregationType.AggregationTypeCount },
  },
};

export const defaultConversionSpec: AverageTypeSpec = {
  averageMetricSpec: {
    aggregation: {
      type: MetricsV1AggregationType.AggregationTypeCount,
      threshold: {
        direction:
          MetricsV1AggregationThresholdDirection.AggregationThresholdDirectionGte,
        threshold: { value: '1' },
      },
    },
  },
};

export const defaultConsumptionSpec: AverageTypeSpec = {
  averageMetricSpec: {
    aggregation: {
      type: MetricsV1AggregationType.AggregationTypeSum,
    },
  },
};

type RatioTypeSpec = { ratioMetricSpec: MetricsV1MetricRatioMetricSpec };

export const defaultRatioSpec: RatioTypeSpec = {
  ratioMetricSpec: {
    numeratorAggregation: {
      type: MetricsV1AggregationType.AggregationTypeCount,
    },
    denominatorAggregation: {
      type: MetricsV1AggregationType.AggregationTypeCount,
    },
  },
};

export const defaultCtrSpec: RatioTypeSpec = {
  ratioMetricSpec: {
    numeratorAggregation: {
      type: MetricsV1AggregationType.AggregationTypeSum,
    },
    numerator: {
      name: '',
    },
    denominatorAggregation: {
      type: MetricsV1AggregationType.AggregationTypeCount,
    },
  },
};

type QuantileTypeSpec = {
  quantileMetricSpec: MetricsV1MetricQuantileMetricSpec;
};

export const defaultQuantileSpec: QuantileTypeSpec = {
  quantileMetricSpec: {
    aggregation: {
      type: MetricsV1AggregationType.AggregationTypeSum,
    },
    quantileLevel: 0.75,
  },
};

export const QUANTILE_LEVELS = [
  { value: 0.05, label: 'P5' },
  { value: 0.1, label: 'P10' },
  { value: 0.25, label: 'P25' },
  { value: 0.5, label: 'P50 (median)' },
  { value: 0.75, label: 'P75' },
  { value: 0.9, label: 'P90' },
  { value: 0.95, label: 'P95' },
];

export const defaultSpecs: Record<MetricType, MetricsV1MetricMetricTypeSpec> = {
  average: defaultAverageSpec,
  conversion: defaultConversionSpec,
  consumption: defaultConsumptionSpec,
  ratio: defaultRatioSpec,
  ctr: defaultCtrSpec,
  quantile: defaultQuantileSpec,
  unknown: {},
};
